import { useState, useEffect, useCallback } from 'react';
import { learnerService, LearnerMastery } from '../services/learnerService';

export interface UseLearnerMasteryReturn {
  mastery: LearnerMastery[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches a learner's mastery map (GET /api/learners/:learnerId/mastery,
 * Phase 2). Every returned row carries confidence + evidenceCount alongside
 * status - the UI requirement is to always show both, never just the label.
 */
export const useLearnerMastery = (learnerId: string | null): UseLearnerMasteryReturn => {
  const [mastery, setMastery] = useState<LearnerMastery[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMastery = useCallback(async () => {
    if (!learnerId) {
      setMastery([]);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const data = await learnerService.getMastery(learnerId, token);
      setMastery(Array.isArray(data) ? data : data.mastery || []);
    } catch (err) {
      console.error('Error fetching learner mastery:', err);
      setError('Failed to load mastery data');
      setMastery([]);
    } finally {
      setLoading(false);
    }
  }, [learnerId]);

  useEffect(() => {
    fetchMastery();
  }, [fetchMastery]);

  return { mastery, loading, error, refetch: fetchMastery };
};
